import React, { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { calculateBoundingBox } from "./utils/domUtils";

const HIGHLIGHT_PADDING = 8; // Padding around the highlighted area in pixels

interface HoverHighlightProps {
    target: string | string[]; // Element(s) to highlight - string for single selector, array for multiple
    hoverTriggerRef?: React.RefObject<HTMLElement>; // Element to attach hover listeners to
    noPadding?: boolean;
}

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

// Animation for the main border (always visible)
const mainBorderAnimation = keyframes`
    0%, 25%, 50%, 75%, 100% {
        border: 3px solid #FFF;
    }
`;

// Two-phase ripple with a 100ms pause at the end:
// - Total: 1500ms (1400ms active + 100ms pause)
// - First half (active): OuterBorder1 grows 0px -> 13px
// - Second half (active): OuterBorder1 grows 13px -> 25px AND OuterBorder2 grows 0px -> 13px
// - Pause: both rings off for the last 100ms, then restart
const HOVER_HIGHLIGHT_ACTIVE_MS = 1400;
const HOVER_HIGHLIGHT_PAUSE_MS = 100;
const HOVER_HIGHLIGHT_ANIM_MS = HOVER_HIGHLIGHT_ACTIVE_MS + HOVER_HIGHLIGHT_PAUSE_MS;

const PADDING_FIRST_RING_PCT = "-13px";
const PADDING_SECOND_RING_PCT = "-25px";

const HALF_ACTIVE_PCT = "45%";
const HALF_ACTIVE_ON_PCT = "46%";
const ACTIVE_END_PCT = "90%";

// @line~68: when the second ring appears, this one should grow to 25px
const outerBorder1Animation = keyframes`
    0% {
        top: 0px;
        left: 0px;
        right: 0px;
        bottom: 0px;
        border-color: rgba(255, 255, 255, 0);
        opacity: 0;
    }
    2% {
        border-color: rgba(255, 255, 255, 0.30);
        opacity: 1;
    }
    ${HALF_ACTIVE_PCT} {
        top: ${PADDING_FIRST_RING_PCT};
        left: ${PADDING_FIRST_RING_PCT};
        right: ${PADDING_FIRST_RING_PCT};
        bottom: ${PADDING_FIRST_RING_PCT};
        border-color: rgba(255, 255, 255, 0.30);
        opacity: 1;
    }
    ${ACTIVE_END_PCT} {
        top: ${PADDING_SECOND_RING_PCT};
        left: ${PADDING_SECOND_RING_PCT};
        right: ${PADDING_SECOND_RING_PCT};
        bottom: ${PADDING_SECOND_RING_PCT};
        border-color: rgba(255, 255, 255, 0.30);
        opacity: 1;
    }
    100% {
        top: ${PADDING_SECOND_RING_PCT};
        left: ${PADDING_SECOND_RING_PCT};
        right: ${PADDING_SECOND_RING_PCT};
        bottom: ${PADDING_SECOND_RING_PCT};
        border-color: rgba(255, 255, 255, 0);
        opacity: 0;
    }
`;

// @line~69: second ring grows to 13px in the second half
const outerBorder2Animation = keyframes`
    0%, ${HALF_ACTIVE_PCT} {
        top: 0px;
        left: 0px;
        right: 0px;
        bottom: 0px;
        border-color: rgba(255, 255, 255, 0);
        opacity: 0;
    }
    ${HALF_ACTIVE_ON_PCT} {
        top: 0px;
        left: 0px;
        right: 0px;
        bottom: 0px;
        border-color: rgba(255, 255, 255, 0.05);
        opacity: 1;
    }
    ${ACTIVE_END_PCT} {
        top: ${PADDING_FIRST_RING_PCT};
        left: ${PADDING_FIRST_RING_PCT};
        right: ${PADDING_FIRST_RING_PCT};
        bottom: ${PADDING_FIRST_RING_PCT};
        border-color: rgba(255, 255, 255, 0.10);
        opacity: 1;
    }
    100% {
        top: ${PADDING_FIRST_RING_PCT};
        left: ${PADDING_FIRST_RING_PCT};
        right: ${PADDING_FIRST_RING_PCT};
        bottom: ${PADDING_FIRST_RING_PCT};
        border-color: rgba(255, 255, 255, 0);
        opacity: 0;
    }
`;

const CustomHighlightOverlay = styled.div<{ boundingBox: { top: number; left: number; width: number; height: number } }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    pointer-events: none;
    animation: ${fadeIn} 600ms ease-out forwards;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        clip-path: polygon(
            0% 0%,
            0% 100%,
            ${props => props.boundingBox.left}px 100%,
            ${props => props.boundingBox.left}px ${props => props.boundingBox.top}px,
            ${props => props.boundingBox.left + props.boundingBox.width}px ${props => props.boundingBox.top}px,
            ${props => props.boundingBox.left + props.boundingBox.width}px ${props => props.boundingBox.top + props.boundingBox.height}px,
            ${props => props.boundingBox.left}px ${props => props.boundingBox.top + props.boundingBox.height}px,
            ${props => props.boundingBox.left}px 100%,
            100% 100%,
            100% 0%
        );
    }
`;

const BorderContainer = styled.div<{ boundingBox: { top: number; left: number; width: number; height: number } }>`
    position: absolute;
    top: ${props => props.boundingBox.top}px;
    left: ${props => props.boundingBox.left}px;
    width: ${props => props.boundingBox.width}px;
    height: ${props => props.boundingBox.height}px;
    pointer-events: none;
`;

const MainBorder = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 3px solid #FFF;
    box-sizing: border-box;
    animation: ${mainBorderAnimation} ${HOVER_HIGHLIGHT_ANIM_MS}ms ease-in-out infinite;
`;

const OuterBorder1 = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 3px solid rgba(255, 255, 255, 0.30);
    border-radius: 2px;
    box-sizing: border-box;
    transform-origin: center;
    will-change: top, left, right, bottom, opacity, border-color;
    animation: ${outerBorder1Animation} ${HOVER_HIGHLIGHT_ANIM_MS}ms linear infinite;
`;

const OuterBorder2 = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 3px solid rgba(255, 255, 255, 0.30);
    border-radius: 2px;
    box-sizing: border-box;
    transform-origin: center;
    will-change: top, left, right, bottom, opacity, border-color;
    animation: ${outerBorder2Animation} ${HOVER_HIGHLIGHT_ANIM_MS}ms linear infinite;
`;

export const HoverHighlight: React.FC<HoverHighlightProps> = ({ target, hoverTriggerRef, noPadding }) => {
    const [isHovering, setIsHovering] = useState(false);
    const [boundingBox, setBoundingBox] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

    const hoverElementRef = useRef<HTMLElement | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const attachListenersTimeoutRef = useRef<number | null>(null);

    const updateBoundingBox = useCallback(() => {
        const boundingBox = calculateBoundingBox(target, noPadding ? 0 : HIGHLIGHT_PADDING);
        setBoundingBox(boundingBox);
    }, [target, noPadding]);

    const startBoundingBoxUpdates = useCallback(() => {
        const updateLoop = () => {
            updateBoundingBox();
            animationFrameRef.current = requestAnimationFrame(updateLoop);
        };
        animationFrameRef.current = requestAnimationFrame(updateLoop);
    }, [updateBoundingBox]);

    const stopBoundingBoxUpdates = useCallback(() => {
        if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);

    const handleMouseEnter = useCallback(() => {
        // Verify target element(s) exist before showing highlight
        const boundingBox = calculateBoundingBox(target, noPadding ? 0 : HIGHLIGHT_PADDING);
        if (boundingBox) {
            setIsHovering(true);
            setBoundingBox(boundingBox);

            // Update bounding box on each animation frame in case elements move/resize
            startBoundingBoxUpdates();
        }
    }, [target, noPadding, startBoundingBoxUpdates]);

    const handleMouseLeave = useCallback(() => {
        setIsHovering(false);
        setBoundingBox(null);
        stopBoundingBoxUpdates();
    }, [stopBoundingBoxUpdates]);

    const detachListeners = useCallback(() => {
        if (hoverElementRef.current) {
            hoverElementRef.current.removeEventListener("mouseenter", handleMouseEnter);
            hoverElementRef.current.removeEventListener("mouseleave", handleMouseLeave);
            hoverElementRef.current = null;
        }
    }, [handleMouseEnter, handleMouseLeave]);

    // Effect to attach/detach listeners when hoverTriggerRef changes
    useEffect(() => {
        // Clear any pending attach timeout
        if (attachListenersTimeoutRef.current !== null) {
            clearTimeout(attachListenersTimeoutRef.current);
            attachListenersTimeoutRef.current = null;
        }

        detachListeners();

        const attachListeners = () => {
            if (hoverTriggerRef?.current) {
                hoverElementRef.current = hoverTriggerRef.current;
                hoverElementRef.current.addEventListener("mouseenter", handleMouseEnter);
                hoverElementRef.current.addEventListener("mouseleave", handleMouseLeave);
            } else {
                // Retry if ref not ready yet
                attachListenersTimeoutRef.current = window.setTimeout(() => {
                    attachListenersTimeoutRef.current = null;
                    attachListeners();
                }, 100);
            }
        };

        // Wait a bit for ref to be ready
        attachListenersTimeoutRef.current = window.setTimeout(() => {
            attachListenersTimeoutRef.current = null;
            attachListeners();
        }, 0);

        // Cleanup function
        return () => {
            detachListeners();
            stopBoundingBoxUpdates();
            if (attachListenersTimeoutRef.current !== null) {
                clearTimeout(attachListenersTimeoutRef.current);
                attachListenersTimeoutRef.current = null;
            }
        };
    }, [hoverTriggerRef, handleMouseEnter, handleMouseLeave, detachListeners, stopBoundingBoxUpdates]);

    if (!isHovering || !boundingBox) {
        return null;
    }

    return (
        <CustomHighlightOverlay boundingBox={boundingBox}>
            <BorderContainer boundingBox={boundingBox}>
                <MainBorder />
                <OuterBorder1 />
                <OuterBorder2 />
            </BorderContainer>
        </CustomHighlightOverlay>
    );
};

